'use client';
import Image from '@/components/Image';
import Switch from '@/components/Form/Switch';
import { useJump } from '@/components/MagicJump';
import Popover from '@/components/Popover';
import { useReporting } from '@/hooks/useReporting';
import { useUserWayWidget } from '@/hooks/useUserWayWidget';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import { IS_DE_FR } from '@/utils/constants';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { Badge } from '@mui/material';
import { QuestionFilled, WapMoreOutlined, WishOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import { HeaderProps } from '.';
import AccountIcon from './components/AccountIcon';
import CartIcon from './components/CartIcon';
import DingTong from './components/DingTong';
import SideNavWrapper from './components/SideNavWrapper.pc';
import SearchInput from './components/SearchComponent/SearchInput';
import SearchPopupPC from './components/SearchComponent/SearchPopup.pc';
import { TopBarSiteConfig } from './components/SiteConfig';
import { useKlarnaPaymentPromotion } from '@/components/Pay/KlarnaPromotion/useKlarnaPaymentPromotion';
import { shouldShowTermsUpdate } from './terms-update';
const SideNavTablet = dynamic(() => import('./components/SideNav.Tablet'), { ssr: false });

const BRAND_NAME = 'ZEELOOL';

const Header = (
  props: HeaderProps & {
    cartProductNum: number | string;
    isShowHeader: boolean;
    isDarkMode: boolean;
    isShowDtBannerByPage: boolean;
    loadSearchDataIfNeeded: () => Promise<void>;
    isBot?: boolean;
  },
) => {
  const { isShowKlarnaPromotion, text_1, klarna_text } = useKlarnaPaymentPromotion();
  const { isShowHeader, isDarkMode, isShowDtBannerByPage, loadSearchDataIfNeeded } = props;
  const publicResourceData = useStore((state) => state.publicResourceData);
  const showTermsUpdate = shouldShowTermsUpdate(publicResourceData?.info?.timestamp);
  const blindMode = useStore((state) => state.waiMode);
  const setBlindMode = useStore((state) => state.setWaiMode);
  const { openUserWayWidget, closeUserWayWidget } = useUserWayWidget();
  const wishNum = useStore((state) => state.wishNum);
  const t = useTranslations('layout.header');
  const tA11y = useTranslations('layout.header.a11y');
  const tSiteConfig = useTranslations('layout.siteConfig');
  const tFloat = useTranslations('layout.rightBottomFloating');
  const router = useRouter();
  const token = useStore((state) => state.token);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [isSearchInputFocus, setIsSearchInputFocus] = useState(false);
  const searchPopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const toJumpTarget = useJump();
  const currencyList = useStore((state) => state.currencyList);
  const currency = currencyList.find((item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY);
  const [isOpenTabletSideNav, setIsOpenTabletSideNav] = useState(false);
  const [secondopoverPosition, setSecondopoverPosition] = useState({
    top: 0,
    left: 0,
  });
  const isMountPopover = useRef(false);
  // 清理定时器，避免内存泄漏
  useEffect(() => {
    return () => {
      if (searchPopupTimeoutRef.current) {
        clearTimeout(searchPopupTimeoutRef.current);
        searchPopupTimeoutRef.current = null;
      }
    };
  }, []);

  const handleWishClick = () => {
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '心愿单',
          url_path: window.location.href,
          page_name: cnName, // 使用路由元数据中的中文名称
          tab_index: 4,
        },
      },
      [ProviderType.SENSORS],
    );
    if (token) {
      router.push('/account/my-wishlist');
    } else {
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'wish_register',
      });
    }
  };
  const publicFreeShipping = (publicResourceData?.config_names as any)
    ?.product_content_free_shipping_member;
  const publicHalfPrice = (publicResourceData?.config_names as any)
    ?.product_content_half_price_member;

  const publicFreeShippingTitle = () => {
    if (publicFreeShipping.status === 1) {
      let title = publicFreeShipping.title || '';
      const rule = publicFreeShipping.rule || {};
      const ordinaryFreeThreshold = rule.ordinary_free_threshold;
      if (title.includes('{threshold_price}') && ordinaryFreeThreshold) {
        title = title.replace('{threshold_price}', ordinaryFreeThreshold);
        title = title.replace(
          '{symbol_prefix}',
          rule.symbol_prefix ?? currency?.symbol_prefix ?? '',
        );
        title = title.replace(
          '{symbol_suffix}',
          rule.symbol_suffix ?? currency?.symbol_suffix ?? '',
        );
      }
      return title;
    }
    return publicFreeShipping.title;
  };

  const publicFreeShippingContent = () => {
    if (publicFreeShipping.status === 1) {
      let content = publicFreeShipping.content || '';
      const rule = publicFreeShipping.rule || {};
      if (
        content.includes('{threshold_price}') &&
        publicFreeShipping.rule.ordinary_free_threshold
      ) {
        content = content.replace(
          '{threshold_price}',
          publicFreeShipping.rule.ordinary_free_threshold,
        );
        content = content.replace(
          '{symbol_prefix}',
          rule.symbol_prefix ?? currency?.symbol_prefix ?? '',
        );
        content = content.replace(
          '{symbol_suffix}',
          rule.symbol_suffix ?? currency?.symbol_suffix ?? '',
        );
      }

      if (
        content.includes('{ordinary_free_country}') &&
        publicFreeShipping.rule.ordinary_free_country
      ) {
        content = content.replace(
          '{ordinary_free_country}',
          publicFreeShipping.rule.ordinary_free_country,
        );
      }

      if (
        content.includes('{ordinary_free_threshold}') &&
        publicFreeShipping.rule.ordinary_free_threshold
      ) {
        content = content.replace(
          '{ordinary_free_threshold}',
          publicFreeShipping.rule.ordinary_free_threshold,
        );
      }

      return content;
    }
    return publicFreeShipping.content;
  };

  const publicHalfPriceTitle = () => {
    if (publicHalfPrice?.rule?.rule_flag === 1) {
      let title = publicHalfPrice.title || '';
      // 替换占位符 {discount_rate} 为实际的折扣率
      if (title.includes('{discount_rate}') && publicHalfPrice.rule.discount_rate) {
        title = title.replace('{discount_rate}', publicHalfPrice.rule.discount_rate);
      }
      return title;
    }
    return publicHalfPrice.title;
  };

  const publicHalfPriceContent = () => {
    if (publicHalfPrice?.rule?.rule_flag === 1) {
      let content = publicHalfPrice.content || '';
      // 替换占位符 {discount_rate} 为实际的折扣率
      if (content.includes('{discount_rate}') && publicHalfPrice.rule.discount_rate) {
        content = content.replace('{discount_rate}', publicHalfPrice.rule.discount_rate);
      }

      if (content.includes('{threshold_price}') && publicHalfPrice.rule.threshold_price) {
        content = content.replace(
          '{threshold_price}',
          formatPriceNumberWithSymbol(publicHalfPrice.rule.threshold_price, currency),
        );
      }

      if (content.includes('{restrict_single}') && publicHalfPrice.rule.restrict_single) {
        content = content.replace('{restrict_single}', publicHalfPrice.rule.restrict_single);
      }
      return content;
    }
    return publicHalfPrice.content;
  };

  const SDSearchEntryBtn_Click = () => {
    trackEvent(
      {
        eventName: 'SearchEntryBtn_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '搜索',
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  // 图标颜色类名
  const iconColorClass = isDarkMode ? 'text-white' : 'text-primary-black';
  return (
    <div
      id="header"
      className={`sticky top-0 z-[400] ${!isShowHeader ? 'hidden' : ''} ${
        isDarkMode
          ? 'bg-[rgba(0,0,0,1)] backdrop-blur-[25px] shadow-none'
          : 'bg-primary-white shadow-[0px_0px_9px_0px_rgba(221,221,221,0.5)]'
      }`}
    >
      <Link href="/accessibility-statement" className="sr-only">
        Accessibility Statement
      </Link>
      {!!props.dtInfo && isShowDtBannerByPage && !isDarkMode && <DingTong {...props} />}
      {!isDarkMode && (
        <ul className="flex h-[40px] w-full items-center justify-between bg-primary-black px-[48px] text-white">
          <li className="flex justify-center items-center h-full transition-all duration-300">
            {publicFreeShipping?.status === 1 && (
              <div className="flex items-center relative text-[14px] leading-[18px]">
                {publicFreeShippingTitle()}
                <Popover
                  id="free-shipping-popover"
                  content={
                    <div className="py-[15px] px-[20px]">
                      <div
                        className="text-[12px] leading-[30px] text-[#808080] break-words"
                        dangerouslySetInnerHTML={{
                          __html: publicFreeShippingContent().replace(/\n/g, '<br/>'),
                        }}
                      />
                    </div>
                  }
                  sx={{
                    '& .MuiPopover-paper': {
                      boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
                      transform: 'translate(-114px, 11px)',
                    },
                  }}
                  triggerComponent={
                    <button
                      type="button"
                      aria-label={tA11y('halfPriceOfferDetails')}
                      className="ml-[6px] inline-flex items-center justify-center border-0 bg-transparent p-0 text-white opacity-60"
                    >
                      <QuestionFilled className="text-[18px]" aria-hidden />
                    </button>
                  }
                  anchorReference="anchorPosition"
                  anchorPosition={{
                    top: 74,
                    left: 248,
                  }}
                  width={400}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  // slotProps={{
                  //   paper: {
                  //     style: {
                  //       transform: 'translate(-114px, 11px) ',
                  //     },
                  //   },
                  // }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  noPadding
                />
              </div>
            )}
            {publicHalfPrice?.status === 1 &&
              publicHalfPrice?.rule &&
              publicHalfPrice?.rule?.rule_flag === 1 && (
                <div
                  className="flex items-center relative text-[14px] leading-[40px] ml-[1.875vw]"
                  ref={(ref) => {
                    if (
                      ref &&
                      secondopoverPosition.top === 0 &&
                      secondopoverPosition.left === 0 &&
                      !isMountPopover.current
                    ) {
                      isMountPopover.current = true;
                      const { top, left } = ref.getBoundingClientRect();
                      setSecondopoverPosition({
                        top,
                        left,
                      });
                    }
                  }}
                >
                  {publicHalfPriceTitle()}
                  <Popover
                    id="half-price-popover"
                    content={
                      <div className="py-[15px] px-[20px]">
                        <div
                          className="text-[12px] leading-[30px] text-[#808080] break-words"
                          dangerouslySetInnerHTML={{
                            __html: publicHalfPriceContent().replace(/\n/g, '<br/>'),
                          }}
                        />
                      </div>
                    }
                    sx={{
                      '& .MuiPopover-paper': {
                        boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
                      },
                    }}
                    triggerComponent={
                      <button
                        type="button"
                        aria-label={tA11y('halfPriceOfferDetails')}
                        className="ml-[6px] inline-flex items-center justify-center border-0 bg-transparent p-0 text-white opacity-60"
                      >
                        <QuestionFilled className="text-[18px]" aria-hidden />
                      </button>
                    }
                    anchorReference="anchorPosition"
                    anchorPosition={{
                      top: secondopoverPosition.top + 40,
                      left: secondopoverPosition.left,
                    }}
                    transformOrigin={{
                      vertical: 0,
                      horizontal: 0,
                    }}
                    width={IS_DE_FR ? 440 : 400}
                    noPadding
                  />
                </div>
              )}
            {isShowKlarnaPromotion && (
              <div className="hidden 2xl:flex items-center relative max-w-[22vw] truncate whitespace-nowrap text-[14px] leading-[18px] ml-[1.875vw]">
                {text_1}
                {klarna_text && (
                  <Popover
                    id="klarna-promotion-header-popover"
                    content={
                      <div className="py-[15px] px-[20px] text-[12px] leading-[18px] text-[#808080] whitespace-pre-line break-words">
                        {klarna_text}
                      </div>
                    }
                    sx={{
                      '& .MuiPopover-paper': {
                        boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
                      },
                    }}
                    triggerComponent={
                      <button
                        type="button"
                        aria-label={tA11y('klarnaPromotionDetails')}
                        className="ml-[6px] inline-flex items-center justify-center border-0 bg-transparent p-0 text-white opacity-60"
                      >
                        <QuestionFilled className="text-[18px]" aria-hidden />
                      </button>
                    }
                    width={400}
                    noPadding
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                  />
                )}
              </div>
            )}
          </li>
          <li className="hidden h-full items-center justify-center text-[12px] leading-[14px] transition-all duration-300 lg:flex">
            {showTermsUpdate && (
              <span
                className="mr-[16px] cursor-pointer whitespace-nowrap opacity-60"
                onClick={() => {
                  trackEvent(
                    {
                      eventName: 'TopNavTab_Click',
                      properties: {
                        tab_name: 'Terms Update',
                        url_path: window.location.href,
                        page_name: cnName,
                        tab_index: '',
                      },
                    },
                    [ProviderType.SENSORS],
                  );
                  toJumpTarget({
                    jump_type: 4,
                    Jump_target: 'https://www.zeelool.com/terms-conditions',
                  });
                }}
              >
                Terms Update
              </span>
            )}
            {props.quickEntryData?.details?.map((item) => (
              <span
                key={item.quick_title}
                className="mr-[16px] cursor-pointer whitespace-nowrap opacity-60"
                onClick={() => {
                  trackEvent(
                    {
                      eventName: 'TopNavTab_Click',
                      properties: {
                        tab_name: item.quick_title,
                        url_path: window.location.href,
                        page_name: cnName, // 使用路由元数据中的中文名称
                        tab_index: '',
                      },
                    },
                    [ProviderType.SENSORS],
                  );
                  toJumpTarget({
                    jump_type: item.jump_type,
                    Jump_target: item.Jump_target,
                  });
                }}
              >
                {item.quick_title}
              </span>
            ))}
            <div className="flex items-center gap-[4px] whitespace-nowrap">
              <span className="opacity-60">
                {blindMode ? tSiteConfig('accessibility') : t('topBar.enableAccessibility')}
              </span>
              <Switch
                checked={blindMode}
                width={26}
                height={16}
                aria-label={
                  blindMode ? tSiteConfig('accessibility') : t('topBar.enableAccessibility')
                }
                onChange={(_event, checked) => {
                  if (checked) {
                    openUserWayWidget();
                  } else {
                    closeUserWayWidget();
                  }
                  setBlindMode(checked);
                  trackEvent(
                    {
                      eventName: 'TopNavTab_Click',
                      properties: {
                        page_name: cnName,
                        url_path: window.location.href,
                        tab_name: `Accessibility_${checked ? 'Open' : 'Close'}`,
                        first_level_category: '',
                      },
                    },
                    [ProviderType.SENSORS],
                  );
                }}
              />
            </div>
            <div className="ml-[24px] flex items-center">
              <Suspense fallback={null}>
                <TopBarSiteConfig />
              </Suspense>
            </div>
          </li>
        </ul>
      )}
      <ul
        className={`h-[60px] relative pl-[44px] pr-[46px] flex justify-between items-center border-b-1 border-b-solid ${isDarkMode ? 'border-b-black' : 'border-b-[#dce0e8]'}`}
      >
        <li className="flex-1 w-0 justify-start flex items-center">
          <button
            type="button"
            className="mr-[20px] block cursor-pointer border-0 bg-transparent p-0 lg:hidden"
            onClick={() => setIsOpenTabletSideNav(true)}
            aria-label={tA11y('openMenu')}
            aria-expanded={isOpenTabletSideNav}
          >
            <WapMoreOutlined className={`text-[24px] ${iconColorClass}`} aria-hidden />
          </button>
          <Link
            prefetch={false}
            href="/"
            aria-label={BRAND_NAME}
            className="relative h-[48px] w-[158px] overflow-hidden flex-shrink-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (window.location.pathname === '/') {
                return;
              }
              trackEvent(
                {
                  eventName: 'TopNavTab_Click',
                  properties: {
                    tab_name: 'Logo',
                    page_name: cnName,
                    url_path: window.location.href,
                    tab_index: 2,
                  },
                },
                [ProviderType.SENSORS],
              );
              router.push('/');
            }}
          >
            {props?.logoData?.logo_image ? (
              <Image
                className="block w-[158px] h-auto object-contain"
                src={isDarkMode ? '/white-logo.png' : props.logoData.logo_image}
                alt={BRAND_NAME}
                width={158}
                height={48}
              />
            ) : (
              <div className="w-[158px]" />
            )}
            {!!props?.logoData?.logo_sub_image && (
              <Image
                className="absolute block w-[158px] h-auto left-0 z-10 object-contain"
                src={props.logoData.logo_sub_image}
                alt={BRAND_NAME}
                width={158}
                height={48}
              />
            )}
          </Link>
          <SideNavWrapper
            navData={props.headerNavList}
            isDarkMode={isDarkMode}
            isBot={props.isBot ?? false}
          />
          <SideNavTablet
            show={isOpenTabletSideNav}
            navData={props.headerNavList}
            quickEntryData={props.quickEntryData}
            onClose={() => setIsOpenTabletSideNav(false)}
          />
        </li>
        <li className="flex items-center justify-center h-full">
          <div
            className={`w-[11.667vw] h-[36px] relative mr-[0.417vw]  transition-all duration-300 rounded-[4px] box-border ${isDarkMode ? 'bg-transparent' : 'bg-[#f5f6f7]'} text-auxiliary-dark-gray text-14 flex items-center`}
          >
            <Suspense fallback={null}>
              <SearchInput
                onClose={() => {
                  setShowSearchPopup(false);
                  setIsSearchInputFocus(false);
                }}
                isShowSearchBtn={false}
                isWeb
                isDarkMode={isDarkMode}
                onFocus={async () => {
                  // 清除可能存在的延时关闭
                  if (searchPopupTimeoutRef.current) {
                    clearTimeout(searchPopupTimeoutRef.current);
                    searchPopupTimeoutRef.current = null;
                  }
                  // 按需加载搜索数据
                  loadSearchDataIfNeeded();
                  setShowSearchPopup(true);
                  setIsSearchInputFocus(true);
                  SDSearchEntryBtn_Click();
                }}
                onBlur={() => {
                  // 延时关闭，给用户时间点击弹窗内容
                  searchPopupTimeoutRef.current = setTimeout(() => {
                    setShowSearchPopup(false);
                    setIsSearchInputFocus(false);
                  }, 150); // 150ms延时
                }}
                onInputChange={(value) => {
                  // 当有输入内容时关闭Popover，避免与自动补全冲突
                  if (value && value.trim().length > 0) {
                    setShowSearchPopup(false);
                  } else if ((!value || value.trim().length === 0) && isSearchInputFocus) {
                    // 当输入为空时，如果输入框有焦点，则显示Popover
                    setShowSearchPopup(true);
                  }
                }}
              />
            </Suspense>

            <SearchPopupPC show={showSearchPopup} onClose={() => setShowSearchPopup(false)} />
          </div>
          <ul className="flex items-center h-full box-border">
            <AccountIcon isDarkMode={isDarkMode} deviceType={props.deviceType} />
            <li className="pl-[24px] relative">
              <button
                type="button"
                className="p-0 m-0 border-0 bg-transparent cursor-pointer flex items-center justify-center h-full"
                onClick={handleWishClick}
                aria-label={t('wishlist')}
              >
                <WishOutlined className={`text-[24px] ${iconColorClass}`} aria-hidden />
              </button>
              {wishNum > 0 && (
                <Badge
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    left: '39px',
                    minWidth: '18px',
                    height: '18px',
                    lineHeight: '18px',
                    padding: '0 5px',
                    boxSizing: 'border-box',
                    borderRadius: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#fff',
                    backgroundColor: colors.primaryPurple,
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontFamily: font.br,
                    '& .MuiBadge-badge.MuiBadge-standard.MuiBadge-invisible': {
                      display: 'none',
                    },
                  }}
                >
                  <span
                    style={{
                      ...(wishNum > 99 && { transform: 'translateY(-20%)' }),
                    }}
                  >
                    {wishNum > 99 ? '...' : wishNum}
                  </span>
                </Badge>
              )}
            </li>
            <CartIcon
              isDarkMode={isDarkMode}
              cartProductNum={props.cartProductNum}
              deviceType={props.deviceType}
            />
          </ul>
        </li>
      </ul>
    </div>
  );
};

export default Header;
